import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ChartLineUp, UsersThree, User, CurrencyDollar, Wallet,
} from "@phosphor-icons/react";

// Campaign Performance — funnel SNAPSHOT (not historical conversion) + ROI.
// Reuses existing data only (leads.campaign_id, customers.acquisition_campaign_id,
// revenue_events). Zero new data collection.

function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export default function CampaignPerformance({ campaignId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${campaignId}/performance`);
      setData(data);
    } catch {
      toast.error("Couldn't load performance");
      setData(null);
    } finally { setLoading(false); }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="text-sm text-ink-secondary" data-testid="campaign-performance-loading">Loading performance…</div>;
  }
  if (!data) {
    return <div className="text-sm text-ink-secondary">Performance unavailable.</div>;
  }

  const funnel = data.funnel || {};
  const roi = data.roi || {};
  const pipelines = funnel.pipelines || [];
  const mm = data.marketing_metrics;
  const noData = (funnel.total_leads || 0) === 0 && (roi.customers_count || 0) === 0;

  const fmtNum = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="space-y-6" data-testid="campaign-performance">
      {noData && (
        <div
          data-testid="campaign-performance-empty"
          className="rounded-md border border-white/10 bg-graphite-950/40 px-4 py-5 text-sm text-ink-secondary"
        >
          No leads or customers linked to this campaign yet. Once leads are created with this campaign set (or when converted leads become customers), the funnel and ROI will fill in here.
        </div>
      )}

      {/* ── ROI Summary ──────────────────────────────────────────────── */}
      <section data-testid="campaign-performance-roi">
        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
          <CurrencyDollar weight="duotone" className="h-3 w-3" /> ROI summary
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4">
            <div className="text-[10px] uppercase tracking-widest text-ink-secondary">Leads</div>
            <div className="mt-1 font-display text-2xl tracking-tight tabular-nums" data-testid="campaign-perf-leads">
              {funnel.total_leads || 0}
            </div>
          </div>
          <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4">
            <div className="text-[10px] uppercase tracking-widest text-ink-secondary">Customers</div>
            <div className="mt-1 font-display text-2xl tracking-tight tabular-nums" data-testid="campaign-perf-customers">
              {roi.customers_count || 0}
            </div>
          </div>
          <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4">
            <div className="text-[10px] uppercase tracking-widest text-ink-secondary">Revenue</div>
            <div className="mt-1 font-display text-2xl tracking-tight tabular-nums text-emerald-200" data-testid="campaign-perf-revenue">
              {fmtMoney(roi.revenue_total || 0)}
            </div>
          </div>
          {roi.roi_ratio !== null && roi.roi_ratio !== undefined ? (
            <div className="rounded-md border border-emerald-400/25 bg-emerald-400/5 p-4" data-testid="campaign-perf-roi-ratio">
              <div className="text-[10px] uppercase tracking-widest text-emerald-200/80 flex items-center gap-1">
                <Wallet weight="duotone" className="h-3 w-3" /> ROI · Rev ÷ Budget
              </div>
              <div className="mt-1 font-display text-2xl tracking-tight tabular-nums text-emerald-200">
                {roi.roi_ratio}×
              </div>
              <div className="mt-0.5 text-[10px] text-ink-secondary/80">
                Budget: {fmtMoney(roi.budget)}
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4" data-testid="campaign-perf-roi-omitted">
              <div className="text-[10px] uppercase tracking-widest text-ink-secondary flex items-center gap-1">
                <Wallet weight="duotone" className="h-3 w-3" /> ROI
              </div>
              <div className="mt-1 text-xs text-ink-secondary">
                Set a budget on the <span className="text-ink-primary">Budget & Timeline</span> tab to see a ROI ratio.
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Marketing metrics (Phase 2) — shown only when linked rows exist ── */}
      {mm && (
        <section data-testid="campaign-perf-marketing-metrics">
          <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
            <ChartLineUp weight="duotone" className="h-3 w-3" /> Marketing metrics · {mm.row_count} linked row{mm.row_count === 1 ? "" : "s"}
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4">
              <div className="text-[10px] uppercase tracking-widest text-ink-secondary">Spend</div>
              <div className="mt-1 font-display text-2xl tracking-tight tabular-nums text-emerald-200" data-testid="campaign-perf-mm-spend">
                {fmtMoney(mm.spend_total)}
              </div>
            </div>
            <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4">
              <div className="text-[10px] uppercase tracking-widest text-ink-secondary">Impressions</div>
              <div className="mt-1 font-display text-2xl tracking-tight tabular-nums" data-testid="campaign-perf-mm-impressions">
                {fmtNum(mm.impressions_total)}
              </div>
            </div>
            <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4">
              <div className="text-[10px] uppercase tracking-widest text-ink-secondary">Clicks</div>
              <div className="mt-1 font-display text-2xl tracking-tight tabular-nums" data-testid="campaign-perf-mm-clicks">
                {fmtNum(mm.clicks_total)}
              </div>
            </div>
            <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4">
              <div className="text-[10px] uppercase tracking-widest text-ink-secondary">CPL</div>
              <div className="mt-1 font-display text-2xl tracking-tight tabular-nums text-amber-200" data-testid="campaign-perf-mm-cpl">
                {mm.cpl === null || mm.cpl === undefined ? "—" : fmtMoney(mm.cpl)}
              </div>
              <div className="mt-0.5 text-[10px] text-ink-secondary/70">
                {mm.cpl === null || mm.cpl === undefined ? "no leads reported" : "spend ÷ leads"}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Funnel Snapshot ──────────────────────────────────────────── */}
      <section data-testid="campaign-performance-funnel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
              <ChartLineUp weight="duotone" className="h-3 w-3" /> Funnel snapshot
            </div>
            <div className="mt-0.5 text-[11px] text-ink-secondary">
              Current distribution of this campaign's leads across pipeline stages —
              <span className="text-amber-200/90"> not a historical conversion rate</span>. Stage transitions aren't tracked over time.
            </div>
          </div>
        </div>

        {pipelines.length === 0 ? (
          <div className="mt-3 text-xs text-ink-secondary">No leads yet — funnel will fill in once leads are linked to this campaign.</div>
        ) : (
          <div className="mt-4 space-y-6">
            {pipelines.map((p) => {
              const maxCount = Math.max(1, ...p.stages.map((s) => s.count || 0));
              return (
                <div key={p.pipeline_id} data-testid={`campaign-perf-pipeline-${p.pipeline_id}`}>
                  <div className="text-xs text-ink-primary flex items-center gap-2">
                    <UsersThree weight="duotone" className="h-3.5 w-3.5 text-ink-secondary" />
                    {p.pipeline_name}
                    <span className="text-ink-secondary text-[11px]">· {p.total_leads_in_pipeline} lead{p.total_leads_in_pipeline === 1 ? "" : "s"}</span>
                  </div>
                  <ol className="mt-2 space-y-1.5">
                    {p.stages.map((s, i) => {
                      const width = Math.round(((s.count || 0) / maxCount) * 100);
                      return (
                        <li
                          key={s.stage_id}
                          data-testid={`campaign-perf-stage-${s.stage_id}`}
                          className="flex items-center gap-3"
                        >
                          <div className="w-32 shrink-0 text-xs text-ink-secondary truncate">{s.stage_name}</div>
                          <div className="flex-1 h-6 rounded-sm border border-white/10 bg-graphite-950 overflow-hidden">
                            <div
                              className="h-full bg-white/10"
                              style={{ width: `${width}%` }}
                            />
                          </div>
                          <div className="w-12 shrink-0 text-right text-sm text-ink-primary tabular-nums" data-testid={`campaign-perf-stage-count-${s.stage_id}`}>
                            {s.count}
                          </div>
                          <div className="w-16 shrink-0 text-right text-[11px] text-ink-secondary tabular-nums" data-testid={`campaign-perf-stage-drop-${s.stage_id}`}>
                            {i === 0
                              ? "—"
                              : (s.drop_pct_from_prev === null || s.drop_pct_from_prev === undefined)
                                ? "—"
                                : `−${s.drop_pct_from_prev}%`}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
